import type {
  DocumentMetadataUserEditValues,
  DocumentPageMetadataUserEditValues,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { MetadataEditScope } from 'features/knowledgeDocumentViewer/types/metadataEditScope'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import {
  type DocumentMetadataDraftFields,
  draftLabelsToDocumentValues,
  DRAWING_NUMBER_FIELD_LABEL,
  isDocumentMetadataEditableLabel,
  isLeaveDocumentFamilyKeyChanged,
  lockedDocumentMetadataLabelsForScope,
  toEditableDocumentDraftFields,
} from 'features/knowledgeDocumentViewer/utils/documentMetadataFieldKeys'
import { getDocumentMetadataCardFields } from 'features/knowledgeDocumentViewer/utils/documentMetadataFields'
import { mergeDocumentMetadataValues } from 'features/knowledgeDocumentViewer/utils/effectiveMetadata'
import type { MetadataDisplayField } from 'features/knowledgeDocumentViewer/utils/pageMetadataFields'
import {
  allowsCrossSurfaceMetadataCascade,
  formatCrossSurfaceCascadeWarning,
  listPageLabelsCascadingFromDocumentEdit,
} from 'features/knowledgeDocumentViewer/utils/sharedMetadataFieldBridge'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export type UseDocumentMetadataEditFormArgs = {
  document: SystemDocumentEntry | undefined
  userEditValues: DocumentMetadataUserEditValues | undefined
  userEditEntryId?: string
  editScope?: MetadataEditScope
  pageBaselineValues?: DocumentPageMetadataUserEditValues
  onSave: (values: DocumentMetadataUserEditValues) => Promise<void>
  onEditingChange?: (isEditing: boolean) => void
}

export type UseDocumentMetadataEditFormResult = {
  isEditing: boolean
  draftFields: DocumentMetadataDraftFields
  editableFields: MetadataDisplayField[]
  lockedLabels: ReadonlySet<string>
  showLeaveFamilyWarning: boolean
  crossSurfaceCascadeWarning: string | undefined
  beginEdit: () => void
  cancelEdit: () => void
  setFieldValue: (label: string, value: string) => void
  saveEdit: () => Promise<void>
}

export function useDocumentMetadataEditForm({
  document,
  userEditValues,
  userEditEntryId,
  editScope,
  pageBaselineValues,
  onSave,
  onEditingChange,
}: UseDocumentMetadataEditFormArgs): UseDocumentMetadataEditFormResult {
  const [isEditing, setIsEditing] = useState(false)
  const [draftFields, setDraftFields] = useState<DocumentMetadataDraftFields>(
    {}
  )
  const [originalDrawingNumber, setOriginalDrawingNumber] = useState('')
  const onEditingChangeRef = useRef(onEditingChange)
  onEditingChangeRef.current = onEditingChange

  const lockedLabels = useMemo(
    () => lockedDocumentMetadataLabelsForScope(editScope),
    [editScope]
  )

  const notifyEditingChange = useCallback((nextIsEditing: boolean) => {
    onEditingChangeRef.current?.(nextIsEditing)
  }, [])

  const clearEditState = useCallback(() => {
    setIsEditing(false)
    setDraftFields({})
    setOriginalDrawingNumber('')
    notifyEditingChange(false)
  }, [notifyEditingChange])

  useEffect(() => {
    clearEditState()
  }, [document?.entry.id, userEditEntryId, clearEditState])

  const beginEdit = useCallback(() => {
    const fields = getDocumentMetadataCardFields(document, userEditValues).map(
      (field) => ({
        label: field.label,
        value: field.value ?? '',
      })
    )
    const draft = toEditableDocumentDraftFields(fields)
    setDraftFields(draft)
    setOriginalDrawingNumber(draft[DRAWING_NUMBER_FIELD_LABEL] ?? '')
    setIsEditing(true)
    notifyEditingChange(true)
  }, [document, notifyEditingChange, userEditValues])

  const cancelEdit = useCallback(() => {
    clearEditState()
  }, [clearEditState])

  const setFieldValue = useCallback((label: string, value: string) => {
    if (!isDocumentMetadataEditableLabel(label)) return
    setDraftFields((prev) => ({ ...prev, [label]: value }))
  }, [])

  const saveEdit = useCallback(async () => {
    const baseline = mergeDocumentMetadataValues(
      document?.entry.data,
      userEditValues
    )
    const values = draftLabelsToDocumentValues(
      draftFields,
      baseline,
      lockedLabels
    )
    try {
      await onSave(values)
    } catch {
      return
    }
    clearEditState()
  }, [
    clearEditState,
    document?.entry.data,
    draftFields,
    lockedLabels,
    onSave,
    userEditValues,
  ])

  const editableFields = getDocumentMetadataCardFields(
    document,
    userEditValues
  ).map((field) => {
    const draftValue = isDocumentMetadataEditableLabel(field.label)
      ? draftFields[field.label]
      : undefined
    return {
      label: field.label,
      value: draftValue ?? field.value ?? '',
    }
  })

  const showLeaveFamilyWarning =
    isEditing &&
    editScope === 'single' &&
    isLeaveDocumentFamilyKeyChanged({
      draftDrawingNumber: draftFields[DRAWING_NUMBER_FIELD_LABEL],
      originalDrawingNumber,
    })

  const crossSurfaceCascadeWarning = useMemo(() => {
    if (!isEditing || pageBaselineValues === undefined) return undefined
    if (!allowsCrossSurfaceMetadataCascade(document?.entry.data.total_pages)) {
      return undefined
    }
    const documentBaseline = mergeDocumentMetadataValues(
      document?.entry.data,
      userEditValues
    )
    const documentNext = draftLabelsToDocumentValues(
      draftFields,
      documentBaseline,
      lockedLabels
    )
    return formatCrossSurfaceCascadeWarning({
      counterpartSurface: 'page',
      labels: listPageLabelsCascadingFromDocumentEdit({
        documentBaseline,
        documentNext,
        pageBaseline: pageBaselineValues,
        requireAlignment: editScope !== 'family',
      }),
    })
  }, [
    document?.entry.data,
    draftFields,
    editScope,
    isEditing,
    lockedLabels,
    pageBaselineValues,
    userEditValues,
  ])

  return {
    isEditing,
    draftFields,
    editableFields,
    lockedLabels,
    showLeaveFamilyWarning,
    crossSurfaceCascadeWarning,
    beginEdit,
    cancelEdit,
    setFieldValue,
    saveEdit,
  }
}
